import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { thumbUrl } from '../utils/imageUrl';

const CITIES = [
  'Bakı', 'Gəncə', 'Sumqayıt', 'Mingəçevir', 'Naxçıvan', 'Lənkəran', 'Şirvan', 'Şəki', 'Yevlax', 'Xankəndi',
  'Abşeron', 'Ağcabədi', 'Ağdam', 'Ağdaş', 'Ağstafa', 'Ağsu', 'Astara', 'Balakən', 'Bərdə', 'Beyləqan',
  'Biləsuvar', 'Cəbrayıl', 'Cəlilabad', 'Daşkəsən', 'Füzuli', 'Gədəbəy', 'Goranboy', 'Göyçay', 'Göygöl',
  'Hacıqabul', 'Hadrut', 'İmişli', 'İsmayıllı', 'Kəlbəcər', 'Kürdəmir', 'Laçın', 'Lerik', 'Masallı', 'Neftçala',
  'Oğuz', 'Qax', 'Qazax', 'Qəbələ', 'Qobustan', 'Quba', 'Qubadlı', 'Qusar', 'Saatlı', 'Sabirabad',
  'Salyan', 'Samux', 'Siyəzən', 'Şamaxı', 'Şəmkir', 'Şuşa', 'Tartar', 'Tovuz', 'Ucar', 'Xaçmaz',
  'Xızı', 'Xocavənd', 'Yardımlı', 'Zaqatala', 'Zəngilan', 'Zərdab',
  'Babək', 'Culfa', 'Kəngərli', 'Ordubad', 'Sədərək', 'Şahbuz', 'Şərur',
];

const BUSINESS_TYPES = [
  { value: 'cafe',       label: 'Kafe' },
  { value: 'restaurant', label: 'Restoran' },
  { value: 'bar',        label: 'Bar' },
  { value: 'bakery',     label: 'Çörəkxana / Patisserie' },
  { value: 'fast_food',  label: 'Fast Food' },
  { value: 'club',       label: 'Klub' },
  { value: 'hookah',     label: 'Qəlyan' },
  { value: 'teahouse',   label: 'Çayxana' },
  { value: 'hotel',      label: 'Otel' },
  { value: 'other',      label: 'Digər' },
];

const emptyBranchForm = () => ({
  name: '', city: 'Bakı', address: '', phone: '', business_type: 'cafe',
});

const emptyMgrForm = () => ({
  mgr_email: '', mgr_password: '', mgr_confirm: '',
});

export default function LocationsPage() {
  const { business } = useAuth();
  const { locations, refreshLocations, isOwner } = useLocation();

  const [saving, setSaving]         = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(emptyBranchForm());
  const [message, setMessage]       = useState('');
  const [managers, setManagers]     = useState([]);

  const [editingId, setEditingId]   = useState(null);
  const [editForm, setEditForm]     = useState({});

  // Manager modal
  const [mgrModal, setMgrModal]     = useState(null); // loc object or null
  const [mgrForm, setMgrForm]       = useState(emptyMgrForm());
  const [mgrSaving, setMgrSaving]   = useState(false);

  useEffect(() => { loadManagers(); }, [business]); // eslint-disable-line

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 5000); };

  const loadManagers = async () => {
    if (!business?.id) return;
    const { data } = await supabase
      .from('business_users')
      .select('*, business_locations(name)')
      .eq('business_id', business.id)
      .order('invited_at', { ascending: false });
    setManagers(data || []);
  };

  // ── Filial yarat (manager olmadan) ───────────────────────────────────────
  const handleSaveBranch = async (e) => {
    e.preventDefault();
    if (!form.name.trim())    { showMsg('❌ Filial adı tələb olunur'); return; }
    if (!form.address.trim()) { showMsg('❌ Ünvan tələb olunur'); return; }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('business_locations')
        .insert([{
          business_id:   business.id,
          name:          form.name.trim(),
          address:       form.address.trim(),
          city:          form.city,
          phone:         form.phone || null,
          is_main:       false,
          is_open:       true,
          business_type: form.business_type || 'cafe',
          premium_tier:  0,
        }]);
      if (error) throw error;

      showMsg('✅ Filial yaradıldı!');
      setShowForm(false);
      setForm(emptyBranchForm());
      refreshLocations();
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Menecer təyin et ─────────────────────────────────────────────────────
  const handleAssignManager = async (e) => {
    e.preventDefault();
    if (!mgrForm.mgr_email.trim())          { showMsg('❌ Email tələb olunur'); return; }
    if (mgrForm.mgr_password.length < 6)    { showMsg('❌ Şifrə ən az 6 simvol olmalıdır'); return; }
    if (mgrForm.mgr_password !== mgrForm.mgr_confirm) { showMsg('❌ Şifrələr uyğun gəlmir'); return; }

    setMgrSaving(true);
    try {
      const { data: { session: ownerSession } } = await supabase.auth.getSession();

      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email: mgrForm.mgr_email.trim().toLowerCase(),
        password: mgrForm.mgr_password,
        options: {
          data: { account_type: 'business_manager' },
          emailRedirectTo: 'https://business.cafegoo.com/login',
        },
      });

      if (signUpErr) throw signUpErr;

      const authUser = signUpData.user;
      if (!authUser) throw new Error('Bu email artıq qeydiyyatdadır');

      await supabase.rpc('confirm_business_auth_user', {
        p_email: mgrForm.mgr_email.trim().toLowerCase(),
      });

      if (signUpData.session) {
        await supabase.auth.signOut();
        if (ownerSession?.access_token) {
          await supabase.auth.setSession({
            access_token:  ownerSession.access_token,
            refresh_token: ownerSession.refresh_token,
          });
        }
      }

      const { error: buErr } = await supabase.rpc('create_branch_manager', {
        p_business_id: business.id,
        p_location_id: mgrModal.id,
        p_user_email:  mgrForm.mgr_email.trim().toLowerCase(),
      });
      if (buErr) throw buErr;

      showMsg(`✅ Menecer təyin edildi: ${mgrForm.mgr_email}`);
      setMgrModal(null);
      setMgrForm(emptyMgrForm());
      loadManagers();
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setMgrSaving(false);
    }
  };

  // ── Redaktə ──────────────────────────────────────────────────────────────
  const startEdit = (loc) => {
    setEditingId(loc.id);
    setEditForm({
      name: loc.name || '',
      city: loc.city || 'Bakı',
      address: loc.address || '',
      phone: loc.phone || '',
      is_open: loc.is_open ?? true,
    });
  };

  const handleEditSave = async (locId) => {
    if (!editForm.name.trim())    { showMsg('❌ Filial adı tələb olunur'); return; }
    if (!editForm.address.trim()) { showMsg('❌ Ünvan tələb olunur'); return; }
    setSaving(true);
    const { error } = await supabase
      .from('business_locations')
      .update({
        name:    editForm.name.trim(),
        city:    editForm.city,
        address: editForm.address.trim(),
        phone:   editForm.phone.trim() || null,
        is_open: editForm.is_open,
      })
      .eq('id', locId);
    setSaving(false);
    if (error) { showMsg('❌ ' + error.message); return; }
    showMsg('✅ Filial məlumatları yeniləndi');
    setEditingId(null);
    refreshLocations();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu filialı silmək istəyirsiniz?')) return;
    const { error } = await supabase.from('business_locations').delete().eq('id', id);
    if (error) { showMsg('❌ ' + error.message); return; }
    showMsg('✅ Filial silindi');
    refreshLocations();
  };

  const handleRemoveManager = async (id) => {
    if (!window.confirm('Bu meneceri silmək istəyirsiniz?')) return;
    await supabase.from('business_users').delete().eq('id', id);
    loadManagers();
  };

  const branchLocations = locations.filter(l => !l.is_main);

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.topRow}>
        <div>
          <h1 style={s.title}>Filiallar</h1>
          <p style={s.sub}>Bütün lokasiyalarınızı bir yerdən idarə edin</p>
        </div>
        {isOwner && (
          business?.is_chain ? (
            <button style={s.addBtn} onClick={() => { setForm(emptyBranchForm()); setShowForm(true); }}>
              + Filial Əlavə Et
            </button>
          ) : (
            <div style={s.lockedBadge}>🔒 Yalnız Chain hesabı</div>
          )
        )}
      </div>

      {message && (
        <div style={{ ...s.msg, background: message.startsWith('❌') ? '#fee2e2' : '#d1fae5', color: message.startsWith('❌') ? '#b91c1c' : '#065f46' }}>
          {message}
        </div>
      )}

      {/* Locked notice */}
      {isOwner && !business?.is_chain && (
        <div style={s.lockedNotice}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🔒</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', marginBottom: 6 }}>Filial yaratmaq mümkün deyil</div>
          <p style={{ margin: 0, color: '#666', fontSize: 13, maxWidth: 400 }}>
            Bu hesab tək kafe kimi qeydiyyatdan keçib. Filial əlavə etmək üçün hesabınızı <b>Chain</b> hesabına yüksəldin.
          </p>
          <div style={{ marginTop: 14, fontSize: 12, color: '#f97316', fontWeight: 600 }}>
            Yüksəltmək üçün dəstəklə əlaqə saxlayın →
          </div>
        </div>
      )}

      {/* ── Yeni Filial Formu ── */}
      {showForm && business?.is_chain && (
        <div style={s.formCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>📍 Yeni Filial Əlavə Et</h3>
            <button onClick={() => { setShowForm(false); setForm(emptyBranchForm()); }}
              style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa' }}>✕</button>
          </div>
          <form onSubmit={handleSaveBranch}>
            <div style={s.grid}>
              <div style={s.field}>
                <label style={s.label}>Filial Adı *</label>
                <input style={s.input} value={form.name} placeholder="Nizami Filialı"
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Şəhər</label>
                <select style={s.input} value={form.city}
                  onChange={e => setForm(p => ({ ...p, city: e.target.value }))}>
                  {CITIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                <label style={s.label}>Ünvan *</label>
                <input style={s.input} value={form.address} placeholder="Küçə adı, bina nömrəsi"
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Kateqoriya</label>
                <select style={s.input} value={form.business_type}
                  onChange={e => setForm(p => ({ ...p, business_type: e.target.value }))}>
                  {BUSINESS_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.label}>Telefon</label>
                <input style={s.input} value={form.phone} placeholder="+994 50 000 00 00"
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button type="button" style={s.cancelBtn}
                onClick={() => { setShowForm(false); setForm(emptyBranchForm()); }}>
                Ləğv Et
              </button>
              <button type="submit" style={s.saveBtn} disabled={saving}>
                {saving ? 'Yaradılır...' : '✓ Filial Yarat'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Filial siyahısı ── */}
      {branchLocations.length === 0 && !showForm ? (
        <div style={s.empty}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📍</div>
          <p style={{ color: '#888', margin: 0 }}>Hələ əlavə filial yoxdur.</p>
        </div>
      ) : (
        branchLocations.map(loc => {
          const isEditing = editingId === loc.id;
          const locMgr = managers.find(m => m.location_id === loc.id && m.role === 'manager');
          return (
            <div key={loc.id} style={s.card}>
              {/* View mode */}
              {!isEditing && (
                <div style={s.cardRow}>
                  {loc.image_url && (
                    <img src={thumbUrl(loc.image_url)} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                      onError={e => e.target.style.display = 'none'} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <div style={s.locName}>{loc.name}</div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: loc.is_open ? '#dcfce7' : '#fee2e2',
                        color: loc.is_open ? '#166534' : '#b91c1c' }}>
                        {loc.is_open ? 'Açıq' : 'Bağlı'}
                      </span>
                    </div>
                    <div style={s.locAddr}>{loc.address}, {loc.city}</div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                      {loc.phone && <span style={s.locDetail}>📞 {loc.phone}</span>}
                    </div>
                    {/* Menecer statusu */}
                    <div style={{ marginTop: 8 }}>
                      {locMgr ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, background: '#d1fae5', color: '#065f46', borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>
                            👤 {locMgr.user_email}
                          </span>
                          <button style={s.removeMgrBtn} onClick={() => handleRemoveManager(locMgr.id)}>✕</button>
                        </div>
                      ) : (
                        <button style={s.assignMgrBtn} onClick={() => { setMgrModal(loc); setMgrForm(emptyMgrForm()); }}>
                          + Menecer Təyin Et
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <button style={s.editBtn} onClick={() => startEdit(loc)}>Düzəlt</button>
                    {isOwner && (
                      <button style={s.deleteBtn} onClick={() => handleDelete(loc.id)}>Sil</button>
                    )}
                  </div>
                </div>
              )}

              {/* Edit mode */}
              {isEditing && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>✏️ {loc.name} — Redaktə</span>
                    <button onClick={() => setEditingId(null)}
                      style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa' }}>✕</button>
                  </div>
                  <div style={s.grid}>
                    <div style={s.field}>
                      <label style={s.label}>Filial Adı *</label>
                      <input style={s.input} value={editForm.name}
                        onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Şəhər</label>
                      <select style={s.input} value={editForm.city}
                        onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))}>
                        {CITIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                      <label style={s.label}>Ünvan *</label>
                      <input style={s.input} value={editForm.address}
                        onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Telefon</label>
                      <input style={s.input} value={editForm.phone} placeholder="+994 50 000 00 00"
                        onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                    <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                      <label style={s.label}>Status</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button"
                          onClick={() => setEditForm(p => ({ ...p, is_open: true }))}
                          style={{ ...s.statusToggle, background: editForm.is_open ? '#dcfce7' : '#f9fafb', color: editForm.is_open ? '#166534' : '#6b7280', border: `2px solid ${editForm.is_open ? '#22c55e' : '#e5e7eb'}` }}>
                          ✅ Açıq
                        </button>
                        <button type="button"
                          onClick={() => setEditForm(p => ({ ...p, is_open: false }))}
                          style={{ ...s.statusToggle, background: !editForm.is_open ? '#fee2e2' : '#f9fafb', color: !editForm.is_open ? '#b91c1c' : '#6b7280', border: `2px solid ${!editForm.is_open ? '#ef4444' : '#e5e7eb'}` }}>
                          ❌ Bağlı
                        </button>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
                    <button style={s.cancelBtn} onClick={() => setEditingId(null)}>Ləğv Et</button>
                    <button style={s.saveBtn} onClick={() => handleEditSave(loc.id)} disabled={saving}>
                      {saving ? 'Saxlanır...' : '✓ Yadda Saxla'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* ── Menecer Təyin Et Modal ── */}
      {mgrModal && (
        <div style={s.overlay} onClick={() => setMgrModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>👤 Menecer Təyin Et</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>{mgrModal.name}</p>
              </div>
              <button onClick={() => setMgrModal(null)}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa' }}>✕</button>
            </div>

            <div style={s.infoBox}>
              Bu məlumatları menecerə verin. Sistemə daxil olub şifrəsini dəyişə bilər.
            </div>

            <form onSubmit={handleAssignManager}>
              <div style={{ ...s.field, marginBottom: 12 }}>
                <label style={s.label}>Menecer Email *</label>
                <input style={s.input} type="email" value={mgrForm.mgr_email}
                  placeholder="menecer@email.com"
                  onChange={e => setMgrForm(p => ({ ...p, mgr_email: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div style={s.field}>
                  <label style={s.label}>Müvəqqəti Şifrə *</label>
                  <input style={s.input} type="text" value={mgrForm.mgr_password}
                    placeholder="min. 6 simvol"
                    onChange={e => setMgrForm(p => ({ ...p, mgr_password: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Şifrəni təsdiqlə *</label>
                  <input style={s.input} type="text" value={mgrForm.mgr_confirm}
                    placeholder="eyni şifrəni yazın"
                    onChange={e => setMgrForm(p => ({ ...p, mgr_confirm: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" style={s.cancelBtn} onClick={() => setMgrModal(null)}>Ləğv Et</button>
                <button type="submit" style={s.saveBtn} disabled={mgrSaving}>
                  {mgrSaving ? 'Yaradılır...' : '✓ Menecer Yarat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page:        { padding: '32px 24px', maxWidth: 820, margin: '0 auto' },
  topRow:      { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16 },
  title:       { margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  sub:         { margin: '4px 0 0', color: '#888', fontSize: 14 },
  addBtn:      { padding: '10px 20px', borderRadius: 10, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0 },
  msg:         { padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: 14, fontWeight: 600 },
  card:        { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '16px 20px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  cardRow:     { display: 'flex', alignItems: 'flex-start', gap: 16 },
  locName:     { fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 },
  locAddr:     { fontSize: 13, color: '#666', marginBottom: 2 },
  locDetail:   { fontSize: 12, color: '#888', marginTop: 2 },
  editBtn:     { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#444', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  statusToggle:{ flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  deleteBtn:   { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#b91c1c', fontSize: 13, cursor: 'pointer' },
  assignMgrBtn:{ padding: '5px 14px', borderRadius: 20, border: '1.5px dashed #d1d5db', background: '#fafafa', color: '#6b7280', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  removeMgrBtn:{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 14, padding: '0 4px' },
  empty:       { textAlign: 'center', padding: '32px 20px', background: '#fafafa', borderRadius: 14, border: '1px dashed #e5e7eb', marginBottom: 16 },
  formCard:    { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '22px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  grid:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  field:       { display: 'flex', flexDirection: 'column', gap: 4 },
  label:       { fontSize: 12, fontWeight: 600, color: '#555' },
  input:       { padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', background: '#fafafa' },
  saveBtn:     { padding: '10px 24px', borderRadius: 10, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  cancelBtn:   { padding: '10px 20px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#666', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  infoBox:     { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1e40af', marginBottom: 16 },
  lockedBadge: { padding: '8px 16px', borderRadius: 10, border: '1.5px dashed #d1d5db', background: '#f9fafb', color: '#9ca3af', fontWeight: 600, fontSize: 13, flexShrink: 0 },
  lockedNotice:{ textAlign: 'center', padding: '32px 24px', background: '#fff7ed', borderRadius: 14, border: '1.5px dashed #fed7aa', marginBottom: 20 },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:       { background: '#fff', borderRadius: 16, padding: '24px', width: '100%', maxWidth: 480, boxShadow: '0 8px 40px rgba(0,0,0,0.15)' },
};
